/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import './app.css-import';
import { createApp } from 'vue';
import VueMatomo from 'vue-matomo';
import { IonicVue } from '@ionic/vue';
import Vue3Tour from 'vue3-tour';
import otuService from './services/onetimeupdate.service';
import bootstrapAppService from './services/bootstrapApp.service';
import sideEffects from './services/sideEffect.service';
import mountGlobalComponents from './services/mountGlobalComponent.service';
import { initializeSentry } from './services/error-handler/sentry/initializeSentry';
import sharedMethods from './mixins/sharedMethods.mixin';
import preBootstrapUpdateService from './services/preBootstrapUpdate.service';
import inAppPurchaseService from './services/inAppPurchase.service';
import capacitorService from './services/capacitor.service';
import appStateService from './services/appState.service';
import ConfigDeviceSettings from './config/device.config';
import ConfigApplicationSettings from './config/application.config';
import EventConst from './constants/event.constant';
import Emitter from './bus';
import { store } from './store/store';
import { initApplication } from './initApplication';
import router from './router';
import App from './App.vue';
import { presentNetworkToast } from './toast/presentNetworkToast';
import { initializePaypal } from './services/paypal/initializePaypal';
import { asyncAuthFlow } from './services/auth/asyncAuthFlow';
import { setNetworkStatus } from './services/connectivity/setNetworkStatus';
import { startNetworkWatcher } from './services/connectivity/startNetworkWatcher';
import { getIsConnectedToInternet } from './services/connectivity/getIsConnectedToInternet';
import { MatomoConfigure } from './config/matomo.config';
import { doMagicLinkLogin } from './services/auth/doMagicLinkLogin';
import { updateBouceLogoText, STAGES } from './services/bouncingLogo.service';
import reportError from './services/error-handler/reportError';

updateBouceLogoText(STAGES.appOpened);
capacitorService.setIsCapacitorMobileApp().then(() => {
  const app = createApp(App);

  app.config.compilerOptions.isCustomElement = (tag) => tag === 'skew-t-component';

  updateBouceLogoText(STAGES.initializeSentry);
  initializeSentry(app);

  let applicationBootstrapped = false;

  app.use(IonicVue)
    .use(router)
    .use(store)
    .use(Vue3Tour)
    .use(VueMatomo, MatomoConfigure(router))
    .mixin(sharedMethods);

  function hidePreLoadContainer() {
    const ele = document.querySelector('.pre-load-container');
    ele.style.display = 'none';
  }
  const mountVueApp = async () => {
    mountGlobalComponents(app);
    await router.isReady();
    app.mount('#app');
    hidePreLoadContainer();
  };

  // This function is used to monitor the operation and report to Sentry if it takes too long
  // https://grok.com/share/bGVnYWN5_a09cd91e-ba5e-471c-8ff7-e2a3ddc7baa2
  function monitorWithTimeout(operationFn, operationName, timeoutMs = 10000) {
    try {
      let isCompleted = false;

      const operationPromise = Promise.resolve(operationFn())
        .then(() => {
          isCompleted = true;
        })
        .catch(error => {
          isCompleted = true;
          throw error; // Re-throw to maintain error handling
        });

      setTimeout(() => {
        if (!isCompleted) {
          reportError('Operation timed out: ' + operationName);
          console.error('Operation timed out:', operationName);
        }
      }, timeoutMs);

      return operationPromise;
    } catch (error) {
      reportError(
        `Error in monitorWithTimeout: ${error.message} for operation: ${operationName}`
      );
      console.error(
        `Error in monitorWithTimeout: ${error.message} for operation: ${operationName}`,
        error
      );
      //throw error;  // Re-throw so the caller can handle it if needed
    }
  }

  function startApplicationBootstrap(onComplete) {
    const TIMEOUT_MS = 1000; // 1 second
    applicationBootstrapped = true;
    (async () => {
      try {
        updateBouceLogoText(STAGES.startApplicationBooting);
        updateBouceLogoText(STAGES.configStatusBar);

        await monitorWithTimeout(
          () => capacitorService.configureStatusBar(),
          'configureStatusBar',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.loadRequiredThings);
        await monitorWithTimeout(
          () => ConfigDeviceSettings.loadRequiredThings(),
          'loadRequiredThings',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.oneTimePreBootstrapUpdate);
        await monitorWithTimeout(
          () => preBootstrapUpdateService.oneTimePreBootstrapUpdate(),
          'oneTimePreBootstrapUpdate',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.doOneTimeUpdate);
        await monitorWithTimeout(
          () => otuService.doOneTimeUpdate(),
          'doOneTimeUpdate',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.loadDeviceSpecificConfig);
        await monitorWithTimeout(
          () => ConfigDeviceSettings.loadDeviceSpecificConfig(),
          'loadDeviceSpecificConfig',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.loadRequiredDataSource);
        await monitorWithTimeout(
          () => bootstrapAppService.loadRequiredDataSource(),
          'loadRequiredDataSource',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.loadApplicationConfig);
        await monitorWithTimeout(
          () => ConfigApplicationSettings.loadApplicationConfig(),
          'loadApplicationConfig',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.initializePaypal);
        await monitorWithTimeout(
          () => initializePaypal(),
          'initializePaypal',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.asyncAuthFlow);
        await monitorWithTimeout(
          () => asyncAuthFlow(),
          'asyncAuthFlow',
          TIMEOUT_MS
        );

        updateBouceLogoText(STAGES.mountVueApp);
        await monitorWithTimeout(
          () => mountVueApp(),
          'mountVueApp',
          TIMEOUT_MS
        );

        initApplication();
        onComplete(null); // Success
      } catch (error) {
        reportError(
          `Bootstrap process aborted: ${error.message}`
        );
        console.error('Bootstrap process aborted:', error.message);
        onComplete(error); // Failure
      }
    })();
  }

  function main() {
    if (applicationBootstrapped === false) {
      updateBouceLogoText(STAGES.doMagiclinkLogin);
      doMagicLinkLogin().then(() => {
        updateBouceLogoText(STAGES.getDeviceInfo);
        capacitorService
          .getDeviceInfo()
          .then(async () => {
            updateBouceLogoText(STAGES.bindApplicationState);
            return await appStateService.bindApplicationState();
          })
          .then(() => {
            updateBouceLogoText(STAGES.startNetworkWatcher);
            return startNetworkWatcher();
          })
          .then(async () => {
            updateBouceLogoText(STAGES.setNetworkStatus);
            return await setNetworkStatus();
          })
          .then(() => {
            updateBouceLogoText(STAGES.getIsConnectedToInternet);
            if (getIsConnectedToInternet()) {
              startApplicationBootstrap(error => {
                if (error) {
                  console.log('Bootstrap failed:', error);
                  reportError({
                    message: 'Bootstrap failed',
                    error,
                  });
                }
              });
              inAppPurchaseService.load();
            } else {
              presentNetworkToast();
            }
          });
      });
    }
  }
  updateBouceLogoText(STAGES.checkInternetAvaialble);
  preBootstrapUpdateService.isBootstrappingAllowed().then(allowed => {
    if (allowed) {
      updateBouceLogoText(STAGES.setAppInstallDate);
      sideEffects().then(main);
    }
  });

  Emitter.on(EventConst.networkBackOnline, main);
});
